/**
 * Reactive gating hooks — Multi-Add Phase 2 (spec §6).
 *
 * A transitory placeholder (unauthenticated bulk-add camera) cannot perform
 * any action — recording, RTMP, NDI, movement/PTZ, routing-to-production —
 * until authenticated. These hooks expose that gate reactively so the controls
 * Guard, the canvas <Interaction>, and the production source lists all suppress
 * affordances for placeholders without re-deriving the rule.
 *
 * Per `think-in-systems` (ai.knowledge/global): one predicate
 * (`device/gating.ts`), two thin reactive adapters here.
 *
 * Per `reference-rules-in-comments` (ai.knowledge/global).
 */
import { useMemo } from 'react';
import type { Device } from '#~/device/index.js';
import { useTransitoryDevice, useTransitoryDeviceIds } from '#~/device/transitory.js';
import type { T_Maybe } from '@tryiris-ai/js-types';

/**
 * Reactive gating check for a single device (spec §6). Returns `true` when
 * the device is a transitory placeholder — the caller must then suppress its
 * action affordance (e.g. the controls Guard).
 *
 * @param deviceId - Device (or placeholder) ID.
 * @returns `true` when the device is gated and affordances must be hidden.
 */
export const useIsDeviceGated = (deviceId: T_Maybe<Device['id']>): boolean => {
  const placeholder = useTransitoryDevice(deviceId);
  return placeholder != null;
};

/**
 * The SET of currently-gated video device ids (spec §6) — transitory
 * placeholders that cannot record, output, or route to production. Used by
 * the canvas (`content.tsx`) and the production source lists
 * (`use-recording`, `use-outputting`) to suppress affordances WITHOUT calling
 * a hook inside a render loop. Memoized so the Set identity is stable while
 * its membership is unchanged.
 */
export const useGatedVideoIds = (): Set<Device['id']> => {
  const transitoryIds = useTransitoryDeviceIds();

  return useMemo(() => new Set<Device['id']>(transitoryIds), [transitoryIds]);
};

/**
 * Apply the §6 gating filter to a device map, PRESERVING REFERENTIAL IDENTITY
 * when nothing is gated.
 *
 * This identity guarantee is the point, not an optimization. The production
 * hooks feed their result into `useMultiDeviceStateQuery` consumers and React
 * Query; handing those a new object identity makes React Query treat the
 * options as changed, re-subscribe its observer, re-render, and recompute —
 * a self-sustaining loop that also thrashes LiveKit peer-connection setup.
 * Returning `source` unchanged when the gated set is empty keeps the exact
 * pre-gating behavior (measured: 2.2 -> 0.19 cores on the multi-add branch).
 *
 * Per `think-in-systems` (ai.knowledge/global): one filter, two consumers —
 * `use-outputting` and `use-recording` previously carried identical copies.
 * Per `reference-rules-in-comments` (ai.knowledge/global).
 *
 * @param source - Device map keyed by device id.
 * @param gatedIds - Ids that must be excluded (see `useGatedVideoIds`).
 * @returns `source` itself when nothing is gated, otherwise a filtered copy.
 */
export const applyVideoGating = <T>(
  source: Record<string, T>,
  gatedIds: ReadonlySet<Device['id']>
): Record<string, T> => {
  if (gatedIds.size === 0) {
    return source;
  }

  return Object.fromEntries(
    Object.entries(source).filter(([id]) => !gatedIds.has(id as Device['id']))
  );
};
